import { useEffect, useMemo, useReducer, useState } from 'react';

import {
  Alert,
  Button,
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraSpacer,
  ChakraText,
  CircularProgress,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  GooglePlacesAutoComplete,
  Modal,
} from '@equip.health/ui';

import SupportLinkedAccountsModal from '~/components/common/SupportLinkedAccountsModal';
import { ApiCommand } from '~/lib/Api';
import { MAX_NAME_LENGTH } from '~/lib/constants';
import { VITE_GOOGLE_PLACES_API_KEY } from '~/lib/constants/env';
import urlConstants from '~/lib/constants/url.constants';
import { GOOGLE_ADDRESS_EMPTY_VALUE } from '~/lib/constants/user.constants';
import { useApi } from '~/lib/hooks';
import useTimezone from '~/lib/hooks/useTimezone';
import util from '~/lib/util/util';
import { useLinkPatientToExistingSupportMutation } from '~/store/services/patients';

import {
  ContactFieldActionTypes,
  ContactFieldReducer,
  DatePickerGridItem,
  DropdownGridItem,
  GridItem,
  MultiSelectGridItem,
  PatientContactReducer,
  PhoneNumberGridItem,
  TextGridItem,
} from './EditGridItems';
import useEnumsForEditDuplicateSupportModal from './useEnumsForEditDuplicateSupportModal';
import {
  getContactRequiredFields,
  isContactAddressFieldsEmpty,
} from '~/lib/util/patientContact.util';

const { neutral } = EquipUIFireflyV2Theme.colors;

const TOP_LEVEL_ID = 'edit-duplicate-support-modal';

const { caption } = EquipUITheme.typography;

const { trim } = util;

const { getLinkedPatientsForEmail: getLinkedPatientsForEmailUrl } =
  urlConstants.patients;

const generateAddressText = (
  address: GooglePlacesField,
  department: string,
) => {
  const addressArray = [
    `${address?.name ?? ''}`,
    `${department || ''}`,
    `${address?.addressLine1 || ''}`,
    `${address?.addressLine2 || ''}`,
    `${address?.city || ''}`,
    `${address?.state || ''}`,
    `${address?.zipCode || ''}`,
  ];

  return (addressArray ?? []).length
    ? addressArray.filter((str) => Boolean(str.length)).join(', ')
    : '';
};

const getGooglePlacesFieldFromPatientContact = (
  address: PatientContact,
): GooglePlacesField =>
  address
    ? {
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        city: address.city,
        country: address.country,
        latitude: address.latitude,
        longitude: address.longitude,
        name: address.affiliationName,
        state: address.state,
        zipCode: address.zip,
      }
    : GOOGLE_ADDRESS_EMPTY_VALUE;

type EditDuplicateSupportModalProps = {
  dismissModal: () => void;
  onUpdate: () => void;
  showEditDuplicateSupportModal: {
    enteredContact: PatientContact;
    existingSupport: PatientContact;
  };
};

const OverlaySpinner = () => (
  <ChakraFlex
    alignItems="center"
    background="white"
    bottom={0}
    justifyContent="center"
    left={0}
    position="absolute"
    right={0}
    top={0}
  >
    <CircularProgress isIndeterminate />{' '}
  </ChakraFlex>
);

const EditDuplicateSupportModal = ({
  dismissModal,
  onUpdate,
  showEditDuplicateSupportModal,
}: EditDuplicateSupportModalProps) => {
  const { enteredContact, existingSupport } =
    showEditDuplicateSupportModal || {};

  const [isLinkedAccountsModalVisible, setLinkedAccountsModalVisible] =
    useState<boolean>(false);

  const {
    contactPreferencesOptions,
    ethnicitiesOptions,
    gendersMenuOptions,
    isGetContactPreferencesLoading,
    isGetEthnicitiesLoading,
    isGetGendersLoading,
    isGetPronounsLoading,
    isGetLanguagesLoading,
    isGetStatesLoading,
    isGetTimezonesLoading,
    languagesMenuOption,
    pronounsMenuOptions,
    statesMenuOptions,
    timezonesMenuOptions,
  } = useEnumsForEditDuplicateSupportModal();

  const [linkPatientToExistingSupport, linkPatientToExistingSupportResult] =
    useLinkPatientToExistingSupportMutation();

  const {
    data: linkedPatientsForEmail,
    loading: isLinkedPatientsForEmailLoading,
    sendRequest: getLinkedPatientsForEmail,
  } = useApi<[SupportOtherLinkedPatient]>();

  useEffect(() => {
    if (existingSupport?.email) {
      getLinkedPatientsForEmail({
        command: ApiCommand.GET,
        url: getLinkedPatientsForEmailUrl(existingSupport.email),
      });
    }
  }, [existingSupport?.email]);

  const linkedPatientsCount = useMemo<number>(
    () => (linkedPatientsForEmail ?? []).length,
    [linkedPatientsForEmail],
  );

  const [contactField, updateContactField] = useReducer<PatientContactReducer>(
    ContactFieldReducer,
    {},
  );

  const [addressField, setAddressField] = useState<GooglePlacesField>(
    GOOGLE_ADDRESS_EMPTY_VALUE,
  );
  const [department, setDepartment] = useState<string>('');
  const [enterManuallySelected, setEnterManually] = useState<boolean>(false);

  const {
    firstName: enteredFirstName,
    cellPhone: enteredPhoneNumber,
    chosenName: enteredChosenName,
    contactPreferences: enteredContactPreferences,
    department: enteredDepartment,
    dateOfBirth: enteredDateOfBirth,
    ethnicities: enteredEthnicities,
    gender: enteredGender,
    languages: enteredLanguages,
    lastName: enteredLastName,
    pronouns: enteredPronouns,
    timezone: enteredTimezone,
  } = enteredContact ?? {};

  const {
    firstName: supportFirstName,
    cellPhone: supportPhoneNumber,
    chosenName: supportChosenName,
    contactPreferences: supportContactPreferences,
    dateOfBirth: supportDateOfBirth,
    department: supportDepartment,
    ethnicities: supportEthnicities,
    gender: supportGender,
    isAccountDeactivated,
    languages: supportLanguages,
    lastName: supportLastName,
    pronouns: supportPronouns,
    timezone: supportTimezone,
  } = existingSupport ?? {};

  const { firstName, lastName, cellPhone } = contactField;
  const { getTimezoneFromLatLong, timezoneData } = useTimezone();

  useEffect(() => {
    updateContactField({
      type: ContactFieldActionTypes.INITIALIZE,
      value: existingSupport,
    });

    setAddressField(
      getGooglePlacesFieldFromPatientContact(existingSupport?.address),
    );
    setDepartment(supportDepartment);
  }, [existingSupport]);

  useEffect(() => {
    if (linkPatientToExistingSupportResult.isSuccess) {
      onUpdate();
      dismissModal();
    }
  }, [linkPatientToExistingSupportResult.isSuccess]);

  const handleAddressChange = (
    updatedAddressField: GooglePlacesField,
  ): void => {
    setAddressField(updatedAddressField);
  };

  const enteredAddressString = useMemo<string>(
    () =>
      generateAddressText(
        getGooglePlacesFieldFromPatientContact(enteredContact),
        department,
      ),
    [enteredContact, enteredDepartment],
  );

  const supportAddressString = useMemo<string>(
    () =>
      generateAddressText(
        getGooglePlacesFieldFromPatientContact(existingSupport?.address),
        supportDepartment,
      ),
    [existingSupport, supportDepartment],
  );

  const isValidPhoneNumber = useMemo<boolean>(
    () => util.validatePhoneNumber(contactField?.cellPhone ?? ''),
    [contactField?.cellPhone],
  );

  const isSaveChangesDisabled = useMemo<boolean>(
    () =>
      !firstName ||
      (firstName ?? '').length > MAX_NAME_LENGTH ||
      !lastName ||
      (lastName ?? '').length > MAX_NAME_LENGTH ||
      (enterManuallySelected && !addressField.state) ||
      (!isContactAddressFieldsEmpty(addressField) && !addressField.state) ||
      !cellPhone ||
      !isValidPhoneNumber,
    [
      firstName,
      lastName,
      enterManuallySelected,
      addressField,
      cellPhone,
      isValidPhoneNumber,
    ],
  );

  const onConfirm = () => {
    const payload = {
      ...enteredContact,
      email: enteredContact.email || '', // TODO the request typings are accurate, but enteredContact typings are weak
      contactExternalId: enteredContact.contactExternalId || '', // these lines get around it for now - we expect these to be there
      addressLine1: addressField.addressLine1,
      addressLine2: addressField.addressLine2,
      affiliationName: addressField.name,
      cellPhone: contactField.cellPhone,
      chosenName: trim(contactField.chosenName),
      city: addressField.city,
      contactPreferences: contactField.contactPreferences,
      country: addressField.country,
      dateOfBirth: contactField.dateOfBirth,
      department,
      ethnicities: contactField.ethnicities,
      firstName: trim(contactField.firstName),
      gender: contactField.gender,
      languages: contactField.languages,
      lastName: trim(contactField.lastName),
      latitude: addressField.latitude,
      longitude: addressField.longitude,
      pronouns: contactField.pronouns,
      state: addressField.state,
      timezone: contactField.timezone,
      zip: addressField.zipCode,
    };

    // TODO preserved this null check - but do we need it?
    if (existingSupport.supportExternalId) {
      linkPatientToExistingSupport({
        supportExternalId: existingSupport.supportExternalId,
        body: payload,
      });
    }
  };

  const getRequiredFields = (): string[] => {
    if (enterManuallySelected) {
      return ['state'];
    } else {
      return getContactRequiredFields(addressField);
    }
  };

  return (
    <Modal
      footerContent={
        <ChakraFlex width="100%">
          <Button
            colorScheme="grey"
            id={`${TOP_LEVEL_ID}__discard`}
            onClick={() => dismissModal()}
            variant="ghost"
          >
            Cancel
          </Button>
          <ChakraSpacer />
          {linkPatientToExistingSupportResult.error &&
            'data' in linkPatientToExistingSupportResult.error && (
              <ChakraText
                {...caption}
                color="critical.base"
                id={`${TOP_LEVEL_ID}__error-message`}
                marginRight="16px"
                marginTop="18px"
              >
                {linkPatientToExistingSupportResult.error.data.toString()}
              </ChakraText>
            )}
          <Button
            id={`${TOP_LEVEL_ID}__save`}
            isDisabled={
              isSaveChangesDisabled ||
              linkPatientToExistingSupportResult.isLoading
            }
            isLoading={linkPatientToExistingSupportResult.isLoading}
            onClick={onConfirm}
          >
            Confirm and grant access to support
          </Button>
        </ChakraFlex>
      }
      hideCloseButton
      id="attachment"
      isOpen={Boolean(showEditDuplicateSupportModal)}
      onClose={() => dismissModal()}
      size="5xl"
      title={
        <ChakraBox width="100%">
          <ChakraBox position="relative">
            <ChakraFlex alignItems="center">
              <Alert fill={neutral.secondary} height="22px" width="19px" />
              <ChakraText
                color="neutral.secondary"
                fontSize="16px"
                fontWeight="500"
                lineHeight="22px"
                marginLeft="8px"
              >
                {`This email is${
                  isAccountDeactivated ? '' : ' currently'
                } used by an existing Support profile`}
              </ChakraText>
            </ChakraFlex>

            <ChakraText
              color="neutral.secondary"
              fontSize="14px"
              fontWeight="400"
              lineHeight="24px"
              marginY="16px"
            >
              <ChakraText>
                {!isAccountDeactivated ? (
                  <>
                    The email <b>{existingSupport?.email}</b> is currently used
                    by an existing Support profile
                    {Boolean(linkedPatientsCount) && (
                      <ChakraText as="span">
                        &nbsp;for&nbsp;
                        <ChakraText
                          as="span"
                          color="primary.100"
                          cursor="pointer"
                          onClick={() => setLinkedAccountsModalVisible(true)}
                        >
                          {linkedPatientsCount}&nbsp;other&nbsp;
                          {linkedPatientsCount > 1 ? 'patients' : 'patient'}
                        </ChakraText>
                      </ChakraText>
                    )}
                    .
                  </>
                ) : (
                  <>
                    The email <b>{existingSupport?.email}</b> is associated to
                    an existing Support profile for at least one other patient,
                    and we need to make sure the profile contains the correct
                    information.{' '}
                    <b>Update and confirm the correct information.</b>
                  </>
                )}
              </ChakraText>
              {!isAccountDeactivated && (
                <>
                  However, the existing profile contains information that is
                  different from what you entered.{' '}
                  <b>Update and confirm the correct information.</b>
                </>
              )}
            </ChakraText>
            {isLinkedPatientsForEmailLoading && <OverlaySpinner />}
          </ChakraBox>
          <ChakraSimpleGrid
            alignItems="center"
            gap="16px"
            height="48px"
            templateColumns="repeat(8, 1fr)"
          >
            <ChakraGridItem colSpan={2} />
            <ChakraGridItem
              color="neutral.secondary"
              colSpan={3}
              fontSize="12px"
              lineHeight="16px"
            >
              On existing Support profile
            </ChakraGridItem>
            <ChakraGridItem
              color="neutral.secondary"
              colSpan={3}
              fontSize="12px"
              fontWeight="400"
              lineHeight="16px"
            >
              You entered
            </ChakraGridItem>
          </ChakraSimpleGrid>
        </ChakraBox>
      }
    >
      <>
        {Boolean(linkedPatientsCount) && (
          <SupportLinkedAccountsModal
            isOpen={isLinkedAccountsModalVisible}
            linkedPatients={linkedPatientsForEmail}
            modalId={TOP_LEVEL_ID}
            onClose={() => setLinkedAccountsModalVisible(false)}
            supportEmail={existingSupport?.email ?? ''}
          />
        )}
        <ChakraBox
          borderBottomStyle="solid"
          borderBottomWidth="1px"
          borderColor="neutral.line"
          borderTopStyle="solid"
          borderTopWidth="1px"
        >
          <TextGridItem
            enteredValue={enteredFirstName}
            id="first-name"
            isRequired
            label="First name"
            supportValue={supportFirstName}
            updateActionTag={ContactFieldActionTypes.UPDATE_FIRST_NAME}
            updateContactField={updateContactField}
            value={contactField.firstName}
          />
          <TextGridItem
            enteredValue={enteredChosenName}
            id="chosen-name"
            label="Chosen name"
            supportValue={supportChosenName}
            updateActionTag={ContactFieldActionTypes.UPDATE_CHOSEN_NAME}
            updateContactField={updateContactField}
            value={contactField.chosenName}
          />
          <TextGridItem
            enteredValue={enteredLastName}
            id="last-name"
            isRequired
            label="Last name"
            supportValue={supportLastName}
            updateActionTag={ContactFieldActionTypes.UPDATE_LAST_NAME}
            updateContactField={updateContactField}
            value={contactField.lastName}
          />
          <DropdownGridItem
            enteredValue={enteredGender}
            id="gender"
            isLoading={isGetGendersLoading}
            label="Gender"
            options={gendersMenuOptions}
            placeholder="Select a gender"
            supportValue={supportGender}
            updateActionTag={ContactFieldActionTypes.UPDATE_GENDER}
            updateContactField={updateContactField}
            value={contactField.gender}
          />

          <MultiSelectGridItem
            enteredValue={enteredPronouns}
            id="pronouns"
            isLoading={isGetPronounsLoading}
            label="Pronouns"
            options={pronounsMenuOptions}
            placeholder="Select pronouns"
            supportValue={supportPronouns}
            updateActionTag={ContactFieldActionTypes.UPDATE_PRONOUNS}
            updateContactField={updateContactField}
            values={(contactField.pronouns ?? []).map((value) => ({
              node: value,
              value,
            }))}
          />

          <DatePickerGridItem
            enteredValue={enteredDateOfBirth}
            label="Date of birth"
            supportValue={supportDateOfBirth}
            updateActionTag={ContactFieldActionTypes.UPDATE_DATE_OF_BIRTH}
            updateContactField={updateContactField}
            value={contactField.dateOfBirth}
          />

          <MultiSelectGridItem
            enteredValue={enteredEthnicities}
            id="ethnicity"
            isLoading={isGetEthnicitiesLoading}
            label="Race/Ethnicity"
            options={ethnicitiesOptions}
            placeholder="Select race/ethnicity"
            supportValue={supportEthnicities}
            updateActionTag={ContactFieldActionTypes.UPDATE_ETHNICITIES}
            updateContactField={updateContactField}
            values={(contactField.ethnicities ?? []).map((value) => ({
              node: value,
              value,
            }))}
          />

          <MultiSelectGridItem
            enteredValue={enteredContactPreferences}
            id="contact-preference"
            isLoading={isGetContactPreferencesLoading}
            label="Contact preference"
            options={contactPreferencesOptions}
            placeholder="Select all that apply..."
            supportValue={supportContactPreferences}
            updateActionTag={ContactFieldActionTypes.UPDATE_CONTACT_PREFERENCE}
            updateContactField={updateContactField}
            values={(contactField.contactPreferences ?? []).map((value) => ({
              node: value,
              value,
            }))}
          />

          <PhoneNumberGridItem
            enteredValue={enteredPhoneNumber}
            isValidPhoneNumber={isValidPhoneNumber}
            label="Cell phone number"
            supportValue={supportPhoneNumber}
            updateActionTag={ContactFieldActionTypes.UPDATE_PHONE_NUMBER}
            updateContactField={updateContactField}
            value={contactField.cellPhone}
          />

          <MultiSelectGridItem
            enteredValue={enteredLanguages}
            id="languages-spoken"
            isLoading={isGetLanguagesLoading}
            label="Language(s) spoken"
            options={languagesMenuOption}
            placeholder="Select all that apply..."
            supportValue={supportLanguages}
            updateActionTag={ContactFieldActionTypes.UPDATE_CONTACT_LANGUAGE}
            updateContactField={updateContactField}
            values={(contactField.languages ?? []).map((value) => ({
              node: value,
              value,
            }))}
          />

          <DropdownGridItem
            enteredValue={enteredTimezone}
            id="timezone"
            isLoading={isGetTimezonesLoading}
            label="Timezone"
            options={timezonesMenuOptions}
            placeholder="Select a timezone"
            supportValue={supportTimezone}
            updateActionTag={ContactFieldActionTypes.UPDATE_TIMEZONE}
            updateContactField={updateContactField}
            value={contactField.timezone}
          />
          <GridItem
            enteredValue={enteredAddressString}
            id="address"
            label="Address/affiliation"
          >
            {enteredAddressString &&
            enteredAddressString === supportAddressString ? (
              <ChakraText>{enteredAddressString ?? 'No answer'}</ChakraText>
            ) : (
              <GooglePlacesAutoComplete
                addressData={addressField}
                apiKey={VITE_GOOGLE_PLACES_API_KEY}
                department={department}
                enterManuallySelected={enterManuallySelected}
                getTimezone={getTimezoneFromLatLong}
                hideNA
                id={TOP_LEVEL_ID}
                isGetStatesLoading={isGetStatesLoading}
                isGetTimezonesLoading={isGetTimezonesLoading}
                label="Address/affiliation"
                requiredFields={getRequiredFields()}
                saveAddressData={(data: GooglePlacesField) =>
                  handleAddressChange(data)
                }
                setEnterManually={(value: boolean) => setEnterManually(value)}
                showAddressFields
                showCountry
                showDepartment
                statesMenuOptions={statesMenuOptions}
                timezoneData={timezoneData}
                timezoneMenuOptions={timezonesMenuOptions}
                updateAddress={(value: string, field: string) => {
                  handleAddressChange({
                    ...addressField,
                    [field]: value,
                  });
                }}
                updateDepartment={(val: string) => {
                  setDepartment(val);
                }}
              />
            )}
          </GridItem>
        </ChakraBox>
      </>
    </Modal>
  );
};

export default EditDuplicateSupportModal;
